import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function useInterval(callback: () => void, delay?: number): void {
	const savedCallback = useRef<() => void>(callback);

	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	useEffect(() => {
		if (!delay) {
			return;
		}

		function tick(): void {
			savedCallback.current();
		}

		const intervalId = setInterval(tick, delay * 1_000);
		return () => clearInterval(intervalId);
	}, [delay]);
}

export function useWindowDimensions(): { readonly height: number | null; readonly width: number | null } {
	const hasWindow = typeof window !== 'undefined';

	const getWindowDimensions = useCallback(() => {
		const width = hasWindow ? window.innerWidth : null;
		const height = hasWindow ? window.innerHeight : null;

		return {
			height,
			width,
		};
	}, [hasWindow]);

	const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions);

	useIsoLayoutEffect(() => {
		if (!hasWindow) {
			return;
		}

		function handleResize(): void {
			setWindowDimensions(getWindowDimensions());
		}

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [getWindowDimensions, hasWindow]);

	return windowDimensions;
}

export function useObserveElementWidth<T extends HTMLElement>(): {
	readonly ref: React.RefObject<T | null>;
	readonly width: number;
} {
	const [width, setWidth] = useState(0);
	const ref = useRef<T>(null);

	useEffect(() => {
		const observer = new ResizeObserver((entries) => {
			setWidth(entries[0]?.contentRect.width ?? 0);
		});

		const current = ref.current;
		if (current) {
			observer.observe(current);
		}

		return () => {
			if (current) {
				observer.unobserve(current);
			}
		};
	}, []);

	return {
		ref,
		width,
	};
}
